import { CoreError, assertSafeInt } from './errors'
import { computeLine, type LineInput } from './lines'
import type { Paisa } from './money'
import { formatDocNumber } from './numbering'
import type { Milli } from './qty'
import { mulDivRound } from './rounding'
import type {
  CreditNoteLineRow,
  CreditNoteRow,
  DocumentLineRow,
  InvoiceLineProductRow,
  PaymentAllocationRow,
  PaymentMethod,
  PaymentRow,
  SalesInvoiceLineRow,
  SalesInvoiceRow,
  StockMovementRow,
  SyncOp,
  SyncRowBase,
} from './rows'
import { computeDocument, type RoundOffMode } from './totals'

// Available in browsers, Workers and Node 19+; declared here so core needs no DOM/Node types.
declare const crypto: { randomUUID(): string }

// Builders turn user input into database rows plus the ops for one sync_push
// tx. Keep every row of one user action in the same tx (the server applies a
// tx atomically and checks header totals against the lines).

export interface BuildContext {
  tenantId: string
  /** ISO timestamp used for created_at/updated_at. */
  now: string
  newId?: () => string
}

export interface Counter {
  id: string
  code: string
  /** 'device' counters number offline; the 'web' counter is numbered by the server. */
  kind: 'device' | 'web'
}

export interface NumberingInput {
  counter: Counter
  prefix: string
  fiscalYear: string
  /** Required for device counters (next_seq from counter_sequences); ignored for web. */
  seq?: number
}

export interface DocLineInput extends LineInput {
  productId?: string | null
  description: string
  taxRateId?: string | null
  /** Default true. False for services or products that don't track stock. */
  trackStock?: boolean
  /** Batch the goods come from (batch-tracked products; see allocateFefo). */
  batchId?: string | null
  /** Serial numbers of the units (serial-tracked products; one per unit). */
  serials?: readonly string[] | null
  /** The product's recipe: ingredients whose stock goes out with the sale. */
  components?: readonly LineComponent[] | null
  /** The line of a quotation or order this line fills (see sales-orders.ts). */
  salesOrderLineId?: string | null
  /** How it was sold (docs/plans/two-units.md); qtyMilli stays in the stock unit. */
  soldUnit?: string | null
  soldQtyMilli?: Milli | null
  /** The product's HS code, kept on the bill line as it is now. */
  hsCode?: string | null
}

/** An ingredient of a recipe: how much one unit of the product uses. */
export interface LineComponent {
  productId: string
  qtyMilli: Milli
}

/** How much of an ingredient a line of `lineQtyMilli` uses (rounded half away from zero). */
export function componentQty(lineQtyMilli: Milli, perUnitMilli: Milli): Milli {
  return mulDivRound(lineQtyMilli, perUnitMilli, 1000)
}

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function numberFields(numbering: NumberingInput | undefined, finalized: boolean) {
  if (!finalized)
    return { counter_id: numbering?.counter.id ?? null, fiscal_year: null, seq: null, number: null }
  if (!numbering) throw new CoreError('numbering_required', 'a finalized document needs a counter')
  const { counter, prefix, fiscalYear, seq } = numbering
  if (counter.kind === 'web') {
    return { counter_id: counter.id, fiscal_year: fiscalYear, seq: null, number: null }
  }
  if (seq === undefined)
    throw new CoreError('numbering_required', 'device counters need the next seq')
  return {
    counter_id: counter.id,
    fiscal_year: fiscalYear,
    seq,
    number: formatDocNumber(prefix, counter.code, fiscalYear, seq),
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

function lineRow(
  ctx: BuildContext,
  lineNo: number,
  input: DocLineInput,
  amounts: ReturnType<typeof computeLine>,
): DocumentLineRow {
  if (input.serials?.length && input.serials.length * 1000 !== input.qtyMilli) {
    throw new CoreError(
      'invalid_input',
      `${input.description}: quantity must equal the number of serials (${input.serials.length})`,
    )
  }
  return {
    ...base(ctx),
    line_no: lineNo,
    product_id: input.productId ?? null,
    description: input.description,
    qty_milli: input.qtyMilli,
    unit_price_paisa: input.unitPricePaisa,
    price_includes_tax: input.priceIncludesTax,
    gross_paisa: amounts.grossPaisa,
    discount_paisa: amounts.discountPaisa,
    tax_rate_id: input.taxRateId ?? null,
    tax_rate_bp: input.taxRateBp,
    taxable_paisa: amounts.taxablePaisa,
    tax_paisa: amounts.taxPaisa,
    total_paisa: amounts.totalPaisa,
    batch_id: input.batchId ?? null,
    serials: input.serials?.length ? [...input.serials] : null,
  }
}

// -----------------------------------------------------------------------------
// Sales invoice
// -----------------------------------------------------------------------------

export interface BuildInvoiceInput {
  id?: string
  status: 'draft' | 'finalized'
  invoiceDate: string
  dueDate?: string | null
  customer?: {
    id?: string | null
    name: string
    pan?: string | null
    address?: string | null
  } | null
  /** Stock is taken from here when finalized. */
  locationId?: string | null
  currency?: string
  lines: readonly DocLineInput[]
  documentDiscountPaisa?: Paisa
  roundOff?: RoundOffMode
  notes?: string | null
  numbering?: NumberingInput
  /** The quotation or order the bill is made from. */
  salesOrderId?: string | null
}

export interface BuiltInvoice {
  invoice: SalesInvoiceRow
  lines: SalesInvoiceLineRow[]
  movements: StockMovementRow[]
  ops: SyncOp[]
}

export function buildInvoice(ctx: BuildContext, input: BuildInvoiceInput): BuiltInvoice {
  if (input.lines.length === 0)
    throw new CoreError('invalid_invoice', 'an invoice needs at least one line')
  const finalized = input.status === 'finalized'
  const doc = computeDocument(input.lines, {
    documentDiscountPaisa: input.documentDiscountPaisa ?? 0,
    roundOff: input.roundOff ?? 'none',
  })

  const invoice: SalesInvoiceRow = {
    ...base(ctx, input.id),
    status: input.status,
    ...numberFields(input.numbering, finalized),
    invoice_date: input.invoiceDate,
    due_date: input.dueDate ?? null,
    customer_id: input.customer?.id ?? null,
    customer_name: input.customer?.name ?? null,
    customer_pan: input.customer?.pan ?? null,
    customer_address: input.customer?.address ?? null,
    location_id: input.locationId ?? null,
    currency: input.currency ?? 'NPR',
    subtotal_paisa: doc.totals.subtotalPaisa,
    discount_paisa: doc.totals.discountPaisa,
    taxable_paisa: doc.totals.taxablePaisa,
    tax_paisa: doc.totals.taxPaisa,
    round_off_paisa: doc.totals.roundOffPaisa,
    total_paisa: doc.totals.totalPaisa,
    notes: input.notes ?? null,
    finalized_at: finalized ? ctx.now : null,
    cancelled_at: null,
    cancel_reason: null,
    sales_order_id: input.salesOrderId ?? null,
  }

  const lines: SalesInvoiceLineRow[] = input.lines.map((l, i) => ({
    ...lineRow(ctx, i + 1, l, doc.lines[i]!),
    invoice_id: invoice.id,
    sales_order_line_id: (input.salesOrderId && l.salesOrderLineId) || null,
    sold_unit: l.soldUnit ?? null,
    sold_qty_milli: l.soldQtyMilli ?? null,
    hs_code: l.hsCode ?? null,
  }))

  const movements: StockMovementRow[] = []
  if (finalized) {
    input.lines.forEach((l) => {
      // A dish takes its ingredients out of stock, whether or not it is counted itself.
      for (const c of l.components ?? []) {
        const qty = componentQty(l.qtyMilli, c.qtyMilli)
        if (qty <= 0) continue
        if (!input.locationId)
          throw new CoreError('location_required', 'choose the location stock is sold from')
        movements.push(
          movement(ctx, c.productId, input.locationId, 'sale', -qty, 'sales_invoice', invoice.id),
        )
      }
      if (!l.productId || l.trackStock === false) return
      if (!input.locationId)
        throw new CoreError('location_required', 'choose the location stock is sold from')
      movements.push(
        movement(
          ctx,
          l.productId,
          input.locationId,
          'sale',
          -l.qtyMilli,
          'sales_invoice',
          invoice.id,
          l.batchId ?? null,
        ),
      )
    })
  }

  return {
    invoice,
    lines,
    movements,
    ops: [
      op('sales_invoices', invoice),
      ...lines.map((r) => op('sales_invoice_lines', r)),
      ...movements.map((r) => op('stock_movements', r)),
    ],
  }
}

function movement(
  ctx: BuildContext,
  productId: string,
  locationId: string,
  type: StockMovementRow['type'],
  qtyMilli: Milli,
  refType: StockMovementRow['ref_type'],
  refId: string,
  batchId: string | null = null,
): StockMovementRow {
  return {
    ...base(ctx),
    product_id: productId,
    location_id: locationId,
    type,
    qty_milli: qtyMilli,
    unit_cost_paisa: null,
    ref_type: refType,
    ref_id: refId,
    notes: null,
    occurred_at: ctx.now,
    batch_id: batchId,
  }
}

/**
 * Cancels a finalized invoice (manager+ on the server). The number is kept;
 * stock taken by the invoice is put back with sale_return movements.
 */
export function cancelInvoice(
  ctx: BuildContext,
  invoice: SalesInvoiceRow,
  originalMovements: readonly StockMovementRow[],
  reason: string,
): { invoice: SalesInvoiceRow; movements: StockMovementRow[]; ops: SyncOp[] } {
  if (invoice.status !== 'finalized')
    throw new CoreError('invalid_transition', 'only finalized invoices can be cancelled')
  if (!reason.trim()) throw new CoreError('invalid_input', 'a cancel reason is required')
  const cancelled: SalesInvoiceRow = {
    ...invoice,
    status: 'cancelled',
    cancelled_at: ctx.now,
    cancel_reason: reason.trim(),
    updated_at: ctx.now,
  }
  // What the invoice still has out: its sales less what already came back
  // (a removed product assignment returns its stock with a sale_return).
  const out = new Map<
    string,
    { productId: string; locationId: string; batchId: string | null; qty: number }
  >()
  for (const m of originalMovements) {
    if (m.ref_type !== 'sales_invoice' || m.ref_id !== invoice.id || m.deleted_at) continue
    if (m.type !== 'sale' && m.type !== 'sale_return') continue
    const batchId = m.batch_id ?? null
    const key = `${m.product_id}|${m.location_id}|${batchId ?? ''}`
    const entry = out.get(key)
    if (entry) entry.qty -= m.qty_milli
    else
      out.set(key, {
        productId: m.product_id,
        locationId: m.location_id,
        batchId,
        qty: -m.qty_milli,
      })
  }
  const movements = [...out.values()]
    .filter((e) => e.qty > 0)
    .map((e) =>
      movement(
        ctx,
        e.productId,
        e.locationId,
        'sale_return',
        e.qty,
        'sales_invoice',
        invoice.id,
        e.batchId,
      ),
    )
  return {
    invoice: cancelled,
    movements,
    ops: [op('sales_invoices', cancelled), ...movements.map((r) => op('stock_movements', r))],
  }
}

// -----------------------------------------------------------------------------
// Products assigned to a custom line
// -----------------------------------------------------------------------------

export interface AssignLineProductsInput {
  invoice: SalesInvoiceRow
  /** A line of that invoice without a product (a custom item). */
  line: SalesInvoiceLineRow
  /** Stock is taken from here. */
  locationId: string
  items: readonly {
    productId: string
    qtyMilli: Milli
    batchId?: string | null
    /** Default true. False for products that don't track stock (cost only). */
    trackStock?: boolean
  }[]
}

/**
 * Says which products a custom line of a finalized invoice really was
 * (manager+ on the server). The bill does not change; the stock goes out now,
 * with sale movements of the invoice.
 */
export function assignLineProducts(
  ctx: BuildContext,
  input: AssignLineProductsInput,
): { assignments: InvoiceLineProductRow[]; movements: StockMovementRow[]; ops: SyncOp[] } {
  const { invoice, line } = input
  if (invoice.status !== 'finalized')
    throw new CoreError('invalid_reference', 'products can only be assigned on a finalized invoice')
  if (line.invoice_id !== invoice.id || line.product_id)
    throw new CoreError('invalid_reference', 'products can only be assigned to a custom line')
  if (input.items.length === 0) throw new CoreError('invalid_input', 'choose at least one product')
  const assignments: InvoiceLineProductRow[] = []
  const movements: StockMovementRow[] = []
  for (const item of input.items) {
    assertSafeInt(item.qtyMilli, 'qtyMilli')
    if (item.qtyMilli <= 0) throw new CoreError('invalid_input', 'quantity must be above zero')
    const batchId = item.batchId ?? null
    assignments.push({
      ...base(ctx),
      invoice_id: invoice.id,
      invoice_line_id: line.id,
      product_id: item.productId,
      qty_milli: item.qtyMilli,
      location_id: input.locationId,
      batch_id: batchId,
    })
    if (item.trackStock === false) continue
    movements.push(
      movement(
        ctx,
        item.productId,
        input.locationId,
        'sale',
        -item.qtyMilli,
        'sales_invoice',
        invoice.id,
        batchId,
      ),
    )
  }
  return {
    assignments,
    movements,
    ops: [
      ...assignments.map((r) => op('invoice_line_products', r)),
      ...movements.map((r) => op('stock_movements', r)),
    ],
  }
}

/** Removes one assigned product and puts its stock back. */
export function unassignLineProduct(
  ctx: BuildContext,
  assignment: InvoiceLineProductRow,
  trackStock = true,
): { movements: StockMovementRow[]; ops: SyncOp[] } {
  const movements = trackStock
    ? [stockReturn(ctx, assignment, 'sales_invoice', assignment.invoice_id)]
    : []
  return {
    movements,
    ops: [
      { table: 'invoice_line_products', op: 'delete', row: { id: assignment.id } },
      ...movements.map((r) => op('stock_movements', r)),
    ],
  }
}

/** A sale_return movement for one assigned product (removing it, or a credit note). */
export function stockReturn(
  ctx: BuildContext,
  assignment: Pick<InvoiceLineProductRow, 'product_id' | 'location_id' | 'qty_milli' | 'batch_id'>,
  refType: 'sales_invoice' | 'credit_note',
  refId: string,
  locationId: string = assignment.location_id,
): StockMovementRow {
  return movement(
    ctx,
    assignment.product_id,
    locationId,
    'sale_return',
    assignment.qty_milli,
    refType,
    refId,
    assignment.batch_id ?? null,
  )
}

// -----------------------------------------------------------------------------
// Credit note
// -----------------------------------------------------------------------------

export interface BuildCreditNoteInput {
  id?: string
  status: 'draft' | 'finalized'
  invoice: SalesInvoiceRow
  /** Lines being returned/credited, with the quantity credited (≤ the invoiced quantity). */
  lines: readonly {
    invoiceLine: SalesInvoiceLineRow
    qtyMilli: Milli
    trackStock?: boolean
    /** The returned units of a serial-tracked line. */
    serials?: readonly string[]
    /** The product's recipe, to put its ingredients back when restocking. */
    components?: readonly LineComponent[] | null
  }[]
  noteDate: string
  reason: string
  restock: boolean
  locationId?: string | null
  roundOff?: RoundOffMode
  numbering?: NumberingInput
}

export interface BuiltCreditNote {
  creditNote: CreditNoteRow
  lines: CreditNoteLineRow[]
  movements: StockMovementRow[]
  ops: SyncOp[]
}

export function buildCreditNote(ctx: BuildContext, input: BuildCreditNoteInput): BuiltCreditNote {
  if (input.invoice.status !== 'finalized')
    throw new CoreError('invalid_reference', 'credit notes need a finalized invoice')
  if (input.lines.length === 0)
    throw new CoreError('invalid_input', 'a credit note needs at least one line')
  if (!input.reason.trim()) throw new CoreError('invalid_input', 'a reason is required')
  const finalized = input.status === 'finalized'

  // Same price and tax treatment as the invoice; the line discount is prorated by quantity.
  const lineInputs: DocLineInput[] = input.lines.map(
    ({ invoiceLine: l, qtyMilli, trackStock, serials, components }) => {
      assertSafeInt(qtyMilli, 'qtyMilli')
      if (qtyMilli <= 0 || qtyMilli > l.qty_milli) {
        throw new CoreError(
          'invalid_input',
          `credited quantity must be between 0 and ${l.qty_milli}`,
        )
      }
      return {
        productId: l.product_id,
        description: l.description,
        qtyMilli,
        unitPricePaisa: l.unit_price_paisa,
        priceIncludesTax: l.price_includes_tax,
        taxRateBp: l.tax_rate_bp,
        taxRateId: l.tax_rate_id,
        discountPaisa: mulDivRound(l.discount_paisa, qtyMilli, l.qty_milli),
        trackStock: trackStock ?? true,
        // Returned goods go back into the batch they were sold from.
        batchId: l.batch_id ?? null,
        serials: serials ?? null,
        components: components ?? null,
      }
    },
  )
  const doc = computeDocument(lineInputs, { roundOff: input.roundOff ?? 'none' })

  const creditNote: CreditNoteRow = {
    ...base(ctx, input.id),
    invoice_id: input.invoice.id,
    status: input.status,
    ...numberFields(input.numbering, finalized),
    note_date: input.noteDate,
    reason: input.reason.trim(),
    restock: input.restock,
    location_id: input.locationId ?? null,
    subtotal_paisa: doc.totals.subtotalPaisa,
    discount_paisa: doc.totals.discountPaisa,
    taxable_paisa: doc.totals.taxablePaisa,
    tax_paisa: doc.totals.taxPaisa,
    round_off_paisa: doc.totals.roundOffPaisa,
    total_paisa: doc.totals.totalPaisa,
    finalized_at: finalized ? ctx.now : null,
    cancelled_at: null,
    cancel_reason: null,
  }

  const lines: CreditNoteLineRow[] = lineInputs.map((l, i) => ({
    ...lineRow(ctx, i + 1, l, doc.lines[i]!),
    credit_note_id: creditNote.id,
    invoice_line_id: input.lines[i]!.invoiceLine.id,
  }))

  const movements: StockMovementRow[] = []
  if (finalized && input.restock) {
    lineInputs.forEach((l) => {
      // A returned dish puts its ingredients back, by the recipe given.
      for (const c of l.components ?? []) {
        const qty = componentQty(l.qtyMilli, c.qtyMilli)
        if (qty <= 0) continue
        if (!input.locationId)
          throw new CoreError('location_required', 'choose the location returned stock goes to')
        movements.push(
          movement(
            ctx,
            c.productId,
            input.locationId,
            'sale_return',
            qty,
            'credit_note',
            creditNote.id,
          ),
        )
      }
      if (!l.productId || l.trackStock === false) return
      if (!input.locationId)
        throw new CoreError('location_required', 'choose the location returned stock goes to')
      movements.push(
        movement(
          ctx,
          l.productId,
          input.locationId,
          'sale_return',
          l.qtyMilli,
          'credit_note',
          creditNote.id,
          l.batchId ?? null,
        ),
      )
    })
  }

  return {
    creditNote,
    lines,
    movements,
    ops: [
      op('credit_notes', creditNote),
      ...lines.map((r) => op('credit_note_lines', r)),
      ...movements.map((r) => op('stock_movements', r)),
    ],
  }
}

// -----------------------------------------------------------------------------
// Payment
// -----------------------------------------------------------------------------

export interface BuildPaymentInput {
  id?: string
  customerId?: string | null
  amountPaisa: Paisa
  method: PaymentMethod
  paymentDate: string
  reference?: string | null
  notes?: string | null
  allocations?: readonly { invoiceId: string; amountPaisa: Paisa }[]
}

export function buildPayment(
  ctx: BuildContext,
  input: BuildPaymentInput,
): { payment: PaymentRow; allocations: PaymentAllocationRow[]; ops: SyncOp[] } {
  assertSafeInt(input.amountPaisa, 'amountPaisa')
  if (input.amountPaisa <= 0)
    throw new CoreError('invalid_input', 'payment amount must be positive')
  const allocated = (input.allocations ?? []).reduce((a, x) => a + x.amountPaisa, 0)
  if (
    (input.allocations ?? []).some(
      (a) => !Number.isSafeInteger(a.amountPaisa) || a.amountPaisa <= 0,
    )
  ) {
    throw new CoreError('invalid_input', 'allocation amounts must be positive')
  }
  if (allocated > input.amountPaisa)
    throw new CoreError('over_allocated', 'allocations exceed the payment amount')

  const payment: PaymentRow = {
    ...base(ctx, input.id),
    customer_id: input.customerId ?? null,
    payment_date: input.paymentDate,
    method: input.method,
    amount_paisa: input.amountPaisa,
    reference: input.reference ?? null,
    notes: input.notes ?? null,
    status: 'active',
    voided_at: null,
    void_reason: null,
  }
  const allocations: PaymentAllocationRow[] = (input.allocations ?? []).map((a) => ({
    ...base(ctx),
    payment_id: payment.id,
    invoice_id: a.invoiceId,
    amount_paisa: a.amountPaisa,
  }))
  return {
    payment,
    allocations,
    ops: [op('payments', payment), ...allocations.map((r) => op('payment_allocations', r))],
  }
}

/**
 * Sets money already received against bills: new allocation rows for existing payments (a
 * payment taken ahead of the bill). The server refuses one that takes a payment past its
 * amount (`over_allocated`) or a bill below zero (`invoice_overpaid`).
 */
export function allocatePayments(
  ctx: BuildContext,
  allocations: readonly { paymentId: string; invoiceId: string; amountPaisa: Paisa }[],
): { allocations: PaymentAllocationRow[]; ops: SyncOp[] } {
  if (allocations.some((a) => !Number.isSafeInteger(a.amountPaisa) || a.amountPaisa <= 0))
    throw new CoreError('invalid_input', 'allocation amounts must be positive')
  const rows: PaymentAllocationRow[] = allocations.map((a) => ({
    ...base(ctx),
    payment_id: a.paymentId,
    invoice_id: a.invoiceId,
    amount_paisa: a.amountPaisa,
  }))
  return { allocations: rows, ops: rows.map((r) => op('payment_allocations', r)) }
}
